import { createElement } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useSetContextualNav } from '@/components/layout/nav-context';
import { useServiceDetail, ServiceAccessError } from '@/api/media';
import { useServiceAccess } from '@/api/service-access';
import { S3_NAV, s3ActiveSlug } from './s3-nav';
import { Overview } from './overview/Overview';
import { BusinessUsagePage } from './usage/BusinessUsagePage';
import { BucketList } from './buckets/BucketList';
import { BucketCreate } from './buckets/BucketCreate';
import { BucketDetail } from './buckets/BucketDetail';
import { ObjectBrowser } from './buckets/objects/ObjectBrowser';
import { AccessKeyList } from './access-keys/AccessKeyList';
import { PolicyList } from './policies/PolicyList';
import { PolicyCreate } from './policies/PolicyCreate';
import { PolicyDetail } from './policies/PolicyDetail';
import { UserList } from './iam/users/UserList';
import { UserCreate } from './iam/users/UserCreate';
import { UserDetail } from './iam/users/UserDetail';
import { GroupList } from './iam/groups/GroupList';
import { GroupCreate } from './iam/groups/GroupCreate';
import { GroupDetail } from './iam/groups/GroupDetail';
import { RoleList } from './iam/roles/RoleList';
import { RoleCreate } from './iam/roles/RoleCreate';
import { RoleDetail } from './iam/roles/RoleDetail';

// Slugs a report-only member may reach. The BE enforces the same set; the FE
// gate hides management entries from the nav and redirects management routes.
const REPORT_SLUGS = new Set(['', 'usage']);

/**
 * Object Storage console — usage overview, buckets (list/create/detail), an
 * object browser per bucket, and read-only access keys for one provisioned
 * storage service. Registers its sidebar in the app's ContextualSidebar (same
 * scope-swap model as the Compute / Cloud Encoding consoles); every sub-page
 * supplies its own page chrome (Container + PageHeader).
 */
export function S3Console() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { serviceId = '' } = useParams<{ serviceId: string }>();
  const service = useServiceDetail(serviceId);
  const base = `/services/oss/${serviceId}`;
  const hrefFor = (slug: string) => (slug ? `${base}/${slug}` : base);

  // Report-only members see Overview and Business Usage only.
  // The BE 403s management routes regardless — this hides them in the UI.
  const { data: accessMode } = useServiceAccess(serviceId);
  const report = accessMode === 'report';

  useSetContextualNav(() => {
    const sub = pathname.slice(base.length).replace(/^\//, '');
    // For report members: keep only groups that have at least one read-only entry.
    const groups = report
      ? S3_NAV.map((group) => ({
          ...group,
          entries: group.entries.filter((e) => REPORT_SLUGS.has(e.slug)),
        })).filter((group) => group.entries.length > 0)
      : S3_NAV;
    return {
      sections: groups.map((group) => ({
        heading: group.heading,
        items: group.entries.map((e) => ({
          id: hrefFor(e.slug),
          label: e.label,
          icon: createElement(e.icon, { size: 16 }),
          onSelect: () => navigate(hrefFor(e.slug)),
        })),
      })),
      activeId: hrefFor(s3ActiveSlug(sub)),
      back: { label: 'Back to Services', onSelect: () => navigate('/services') },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, pathname, navigate, report]);

  // No access / no such service (hard HTTP or soft 200-body status) — bounce to
  // the services dashboard, same as the other service consoles.
  const errStatus =
    service.error instanceof ServiceAccessError
      ? service.error.status
      : isAxiosError(service.error)
        ? service.error.response?.status
        : undefined;
  if (errStatus === 403 || errStatus === 404) return <Navigate to="/services" replace />;

  // Redirects report members away from management routes to the console base.
  const managed = (el: JSX.Element) => (report ? <Navigate to={base} replace /> : el);

  return (
    <Routes>
      <Route index element={<Overview serviceId={serviceId} />} />
      <Route path="buckets" element={managed(<BucketList serviceId={serviceId} />)} />
      <Route path="buckets/create" element={managed(<BucketCreate serviceId={serviceId} />)} />
      <Route path="buckets/:bucket" element={managed(<BucketDetail serviceId={serviceId} />)} />
      <Route path="buckets/:bucket/objects" element={managed(<ObjectBrowser serviceId={serviceId} />)} />
      <Route path="usage" element={<BusinessUsagePage serviceId={serviceId} />} />
      <Route path="access-keys" element={managed(<AccessKeyList serviceId={serviceId} />)} />
      <Route path="policies" element={managed(<PolicyList serviceId={serviceId} />)} />
      <Route path="policies/create" element={managed(<PolicyCreate serviceId={serviceId} />)} />
      <Route path="policies/:arn" element={managed(<PolicyDetail serviceId={serviceId} />)} />
      <Route path="iam/users" element={managed(<UserList serviceId={serviceId} />)} />
      <Route path="iam/users/create" element={managed(<UserCreate serviceId={serviceId} />)} />
      <Route path="iam/users/:name" element={managed(<UserDetail serviceId={serviceId} />)} />
      <Route path="iam/groups" element={managed(<GroupList serviceId={serviceId} />)} />
      <Route path="iam/groups/create" element={managed(<GroupCreate serviceId={serviceId} />)} />
      <Route path="iam/groups/:name" element={managed(<GroupDetail serviceId={serviceId} />)} />
      <Route path="iam/roles" element={managed(<RoleList serviceId={serviceId} />)} />
      <Route path="iam/roles/create" element={managed(<RoleCreate serviceId={serviceId} />)} />
      <Route path="iam/roles/:name" element={managed(<RoleDetail serviceId={serviceId} />)} />
      <Route path="*" element={<Overview serviceId={serviceId} />} />
    </Routes>
  );
}

export default S3Console;
